import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Box, Button as ThemeButton, Checkbox, Flex, Label as ThemeLabel, Text, useColorMode } from "theme-ui";

interface PreviewColumn {
    header: string;
    accessor: (item: any) => any;
}

interface CommonExportModalProps {
    isOpen: boolean;
    toggle: () => void;
    columns: any[];
    selectedData: any[];
    onExport?: () => void;
    title: string;
    sheetName: string;
    fileNamePrefix: string;
    previewColumns: PreviewColumn[];
}

const CommonExportModal: React.FC<CommonExportModalProps> = ({
    isOpen,
    toggle,
    columns,
    selectedData,
    onExport,
    title,
    sheetName,
    fileNamePrefix,
    previewColumns
}) => {
    const [colorMode] = useColorMode();
    const isDark = colorMode === 'dark';

    const bgModal = isDark ? "#20283c" : "#ffffff";
    const textModal = isDark ? "#dadada" : "#453521";
    const borderModal = isDark ? "#2e354d" : "#dee2e6";
    const partitionColor = isDark ? "#ffffff" : "#dee2e6";

    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

    const availableColumns = useMemo(() =>
        columns.filter((col: any) => col.header && (col.accessorKey || col.id) && !['action', 'No', 'Select', 'select', 'drag-handle'].includes(col.header) && !['select', 'drag-handle', 'no', 'action'].includes(col.id || col.accessorKey)),
        [columns]);

    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        if (isOpen && !hasInitialized) {
            // Initially select all columns
            setSelectedColumns(availableColumns.map((col: any) => col.accessorKey || col.id));
            // Pre-select all data items
            const allIds = Array.from(new Set(selectedData.map((t: any) => String(t._id))));
            setSelectedItemIds(allIds);
            setHasInitialized(true);
        } else if (!isOpen) {
            setHasInitialized(false);
        }
    }, [isOpen, availableColumns, selectedData, hasInitialized]);

    const toggleModal = useCallback(() => {
        toggle();
    }, [toggle]);

    const handleSelectAllColumns = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedColumns(availableColumns.map((col: any) => col.accessorKey || col.id));
        } else {
            setSelectedColumns([]);
        }
    }, [availableColumns]);

    const handleColumnToggle = useCallback((accessor: string) => {
        setSelectedColumns((prev: string[]) =>
            prev.includes(accessor) ? prev.filter((a: string) => a !== accessor) : [...prev, accessor]
        );
    }, []);

    const handleExport = useCallback(() => {
        if (selectedItemIds.length === 0) {
            toast.error("please select items to export");
            return;
        }

        if (selectedColumns.length === 0) {
            toast.error("Please select at least one column");
            return;
        }

        // Filter selected data by the items chosen in the modal
        const dataToExport = selectedData.filter((item: any) =>
            selectedItemIds.includes(String(item._id))
        );

        // Prepare data for XLSX
        const exportData = dataToExport.map((item: any) => {
            const row: any = {};

            availableColumns
                .filter((col: any) => selectedColumns.includes(col.accessorKey || col.id))
                .forEach((col: any) => {
                    const accessor = col.accessorKey || col.id;
                    let value: any = '-';

                    if (col.exportValue && typeof col.exportValue === 'function') {
                        value = col.exportValue(item);
                    } else if (accessor) {
                        // Generic deep access
                        value = accessor.split('.').reduce((acc: any, part: string) => acc && acc[part], item);
                    }

                    // Handle empty/zero cases
                    if (value === 0 || value === '0') {
                        row[accessor] = value;
                    } else if (!value || value.toString().trim() === '') {
                        row[accessor] = '-';
                    } else {
                        row[accessor] = value;
                    }
                });
            return row;
        });

        // Create workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        // Define columns
        const exportCols = availableColumns.filter((col: any) => selectedColumns.includes(col.accessorKey || col.id));
        worksheet.columns = exportCols.map((col: any) => ({
            header: col.header as string,
            key: col.accessorKey || col.id,
            width: 15
        }));

        // Add rows
        exportData.forEach((row: any) => {
            worksheet.addRow(row);
        });

        // Style header row
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF3F3F3' }
        };

        // Apply borders and dynamic widths
        worksheet.columns.forEach((column: any) => {
            let maxLength = 0;
            if (column.header && column.header.length > maxLength) {
                maxLength = column.header.length;
            }
            column.eachCell!({ includeEmpty: true }, (cell: any) => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                    left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                    bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                    right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                const cellValueStr = cell.value ? cell.value.toString() : '';
                const columnLength = cellValueStr.length;
                if (columnLength > maxLength) {
                    maxLength = columnLength;
                }
            });
            column.width = Math.min(Math.max(maxLength, 10) + 2, 60);
        });

        // Generate Excel file and trigger download
        workbook.xlsx.writeBuffer().then((buffer: any) => {
            saveAs(new Blob([buffer]), `${fileNamePrefix}_Export_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
        }).catch((err: any) => {
            console.error("Export failed:", err);
            toast.error("Failed to export Excel file");
        });

        onExport?.();
        toggleModal();
    }, [selectedItemIds, selectedColumns, availableColumns, selectedData, toggleModal, onExport, sheetName, fileNamePrefix]);

    const closeButton = (
        <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={toggleModal}
            style={{
                filter: isDark ? 'invert(1) grayscale(100%) brightness(200%)' : 'none',
            }}
        />
    );

    return (
        <Modal isOpen={isOpen} toggle={toggleModal} centered size="lg">
            <ModalHeader toggle={toggleModal} close={closeButton} style={{ background: bgModal, color: textModal, borderBottom: `1px solid ${borderModal}` }}>
                <Text variant="Inter20Bold">{title}</Text>
            </ModalHeader>
            <ModalBody style={{ background: bgModal, color: textModal }}>
                <Box mb={4}>
                    <ThemeLabel variant="Inter16Bold" sx={{ display: 'block', mb: 2, color: textModal }}>
                        Selected Items ({selectedData.length})
                    </ThemeLabel>
                    <Box sx={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: `1px solid ${borderModal}`,
                        borderRadius: '8px',
                        '.custom-table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            'th, td': {
                                padding: '12px',
                                borderBottom: `1px solid ${borderModal}`,
                                borderRight: `1px solid ${partitionColor}`,
                                textAlign: 'left',
                                fontSize: '14px'
                            },
                            'th:last-child, td:last-child': {
                                borderRight: 'none'
                            },
                            'th': {
                                backgroundColor: isDark ? '#2e354d' : '#f8f9fa',
                                fontWeight: 'bold',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            },
                            'tr:last-child td': {
                                borderBottom: 'none'
                            }
                        }
                    }}>
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedItemIds.length === selectedData.length && selectedData.length > 0}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedItemIds(selectedData.map((t: any) => String(t._id)));
                                                } else {
                                                    setSelectedItemIds([]);
                                                }
                                            }}
                                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                        />
                                    </th>
                                    {previewColumns.map(col => (
                                        <th key={col.header}>{col.header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedData.map((item: any) => {
                                    const itemId = String(item._id);
                                    const isChecked = selectedItemIds.includes(itemId);
                                    return (
                                        <tr key={itemId}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setSelectedItemIds(prev =>
                                                            checked
                                                                ? Array.from(new Set([...prev, itemId]))
                                                                : prev.filter(id => id !== itemId)
                                                        );
                                                    }}
                                                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                />
                                            </td>
                                            {previewColumns.map(col => (
                                                <td key={col.header}>{col.accessor(item) || '-'}</td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                </Box>

                <Box>
                    <ThemeLabel variant="Inter16Bold" sx={{ display: 'block', mb: 2, color: textModal }}>
                        Select Columns
                    </ThemeLabel>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'],
                        gap: 2,
                        border: `1px solid ${borderModal}`,
                        p: 3,
                        borderRadius: 4
                    }}>
                        <Flex sx={{ gridColumn: '1 / -1', borderBottom: `1px solid ${borderModal}`, pb: 2, mb: 2, alignItems: 'center' }}>
                            <ThemeLabel variant="Inter14Bold" sx={{ display: 'flex', alignItems: 'center', mb: 0, cursor: 'pointer', color: textModal, width: '100%' }}>
                                <Checkbox
                                    checked={selectedColumns.length === availableColumns.length}
                                    onChange={handleSelectAllColumns}
                                />
                                <Text sx={{ ml: 2 }}>Select All</Text>
                            </ThemeLabel>
                        </Flex>
                        {availableColumns.map((col: any) => {
                            const accessor = col.accessorKey || col.id;
                            return (
                                <Flex key={accessor} sx={{ alignItems: 'center', py: 1, width: "max-content" }}>
                                    <ThemeLabel variant="Inter14Regular" sx={{ display: 'flex', alignItems: 'center', mb: 0, cursor: 'pointer', color: textModal, width: '100%' }}>
                                        <Checkbox
                                            checked={selectedColumns.includes(accessor)}
                                            onChange={() => handleColumnToggle(accessor)}
                                        />
                                        <Text sx={{ ml: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {col.header}
                                        </Text>
                                    </ThemeLabel>
                                </Flex>
                            );
                        })}
                    </Box>
                </Box>
            </ModalBody>
            <ModalFooter style={{ background: bgModal, borderTop: `1px solid ${borderModal}` }}>
                <ThemeButton
                    variant="OutlineButton"
                    sx={{
                        background: 'transparent',
                        borderColor: borderModal,
                        color: textModal,
                        px: 4,
                        mr: 2,
                        '&:hover': {
                            bg: isDark ? '#2e354d' : '#f0f0f0'
                        }
                    }}
                    onClick={toggleModal}
                >
                    Cancel
                </ThemeButton>
                <ThemeButton
                    variant="Inter16Bold"
                    sx={{
                        background: '#7743db',
                        color: '#ffffff',
                        px: 4,
                        cursor: (selectedItemIds.length > 0 && selectedColumns.length > 0) ? 'pointer' : 'not-allowed',
                        opacity: (selectedItemIds.length > 0 && selectedColumns.length > 0) ? 1 : 0.6
                    }}
                    disabled={selectedItemIds.length === 0 || selectedColumns.length === 0}
                    onClick={handleExport}
                >
                    Export Excel
                </ThemeButton>
            </ModalFooter>
        </Modal >
    );
};

export default memo(CommonExportModal);
